import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Lightbulb,
  CheckSquare,
  Map,
  Network,
  ClipboardList,
  Rocket,
  Handshake,
  ClipboardCheck,
  BarChart3,
  Calendar,
  Settings,
  Search,
  DollarSign,
  Target,
  Shield,
  Brain,
  Focus
} from "lucide-react";

const ITConsultingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsultation = () => {
    navigate("/#contact");
  };

  return (
    <div className="min-h-screen" data-testid="it-consulting-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/services" className="hover:text-blue-600 transition-colors">Services</Link>
              <span className="mx-2">›</span>
              <span>IT Consulting</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              IT Consulting & Strategy
            </h1>
            <p className="text-lg" style={{ color: "#4A5568", maxWidth: "700px", margin: "0 auto" }}>
              Expert guidance to align technology with your business goals
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-20 px-5 bg-white" data-testid="hero-section">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="flex justify-center">
                <Lightbulb size={180} style={{ color: "#3B82F6" }} className="hidden md:block" />
                <Lightbulb size={120} style={{ color: "#3B82F6" }} className="md:hidden" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: "#1F2937" }}>
                  Strategic IT Guidance When You Need It Most
                </h2>
                <p className="text-lg mb-8 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Whether you're planning a major technology upgrade, dealing with ongoing IT challenges, or simply want to ensure your technology supports your business goals, our IT consulting services provide the expertise you need.
                </p>
                <Button
                  onClick={handleConsultation}
                  data-testid="hero-cta"
                  className="text-lg font-semibold px-8 py-6 rounded-md"
                  style={{ backgroundColor: "#3B82F6" }}
                >
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* When to Consider Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="when-to-consider">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
              When Should You Consider IT Consulting?
            </h2>
            <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
              We can help if you're experiencing any of these situations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              <div>
                <ChecklistItem text="Planning a major technology upgrade or replacement" />
                <ChecklistItem text="Opening a new office location" />
                <ChecklistItem text="Experiencing frequent IT problems" />
                <ChecklistItem text="Concerned about cybersecurity risks" />
                <ChecklistItem text="Unsure if your technology supports business growth" />
                <ChecklistItem text="Need help with IT budget planning" />
              </div>
              <div>
                <ChecklistItem text="Dealing with compliance requirements" />
                <ChecklistItem text="Considering cloud migration" />
                <ChecklistItem text="Merging with or acquiring another business" />
                <ChecklistItem text="Evaluating new software or systems" />
                <ChecklistItem text="Need an independent technology assessment" />
                <ChecklistItem text="Want to modernize your IT infrastructure" />
              </div>
            </div>
            <p className="text-lg italic text-center mt-10 font-semibold" style={{ color: "#2C5F2D" }}>
              If any of these apply to your business, let's talk about how we can help.
            </p>
          </div>
        </section>

        {/* Consulting Services Section */}
        <section className="py-20 px-5 bg-white" data-testid="consulting-services">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Our IT Consulting Services
            </h2>
            
            {/* Service 1 */}
            <div className="rounded-lg p-8 md:p-10 mb-10" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #3B82F6" }}>
              <Map size={48} style={{ color: "#3B82F6" }} className="mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ color: "#1F2937" }}>IT Strategy & Planning</h3>
              <p className="text-lg mb-6" style={{ color: "#4A5568" }}>Align technology with your business objectives</p>
              <h4 className="text-lg font-bold mb-4" style={{ color: "#1F2937" }}>What we do:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BulletItem text="Create a technology roadmap" color="#3B82F6" />
                <BulletItem text="Develop multi-year IT plans" color="#3B82F6" />
                <BulletItem text="Budget planning and forecasting" color="#3B82F6" />
                <BulletItem text="Vendor selection and management" color="#3B82F6" />
                <BulletItem text="Technology lifecycle planning" color="#3B82F6" />
                <BulletItem text="ROI analysis" color="#3B82F6" />
              </div>
            </div>

            {/* Service 2 */}
            <div className="rounded-lg p-8 md:p-10 mb-10" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #2C5F2D" }}>
              <Network size={48} style={{ color: "#2C5F2D" }} className="mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ color: "#1F2937" }}>Infrastructure Assessment</h3>
              <p className="text-lg mb-6" style={{ color: "#4A5568" }}>Evaluate and optimize your IT environment</p>
              <h4 className="text-lg font-bold mb-4" style={{ color: "#1F2937" }}>What we do:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BulletItem text="Network performance analysis" color="#2C5F2D" />
                <BulletItem text="Security vulnerability assessment" color="#2C5F2D" />
                <BulletItem text="Hardware and software inventory" color="#2C5F2D" />
                <BulletItem text="Identify single points of failure" color="#2C5F2D" />
                <BulletItem text="Capacity planning" color="#2C5F2D" />
                <BulletItem text="Detailed recommendations report" color="#2C5F2D" />
              </div>
            </div>

            {/* Service 3 */}
            <div className="rounded-lg p-8 md:p-10 mb-10" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #3B82F6" }}>
              <ClipboardList size={48} style={{ color: "#3B82F6" }} className="mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ color: "#1F2937" }}>Technology Selection</h3>
              <p className="text-lg mb-6" style={{ color: "#4A5568" }}>Choose the right solutions for your business</p>
              <h4 className="text-lg font-bold mb-4" style={{ color: "#1F2937" }}>What we do:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BulletItem text="Requirements gathering" color="#3B82F6" />
                <BulletItem text="Software evaluation" color="#3B82F6" />
                <BulletItem text="Hardware recommendations" color="#3B82F6" />
                <BulletItem text="Cloud vs. on-premise analysis" color="#3B82F6" />
                <BulletItem text="Cost-benefit analysis" color="#3B82F6" />
                <BulletItem text="Proof of concept testing" color="#3B82F6" />
              </div>
            </div>

            {/* Service 4 */}
            <div className="rounded-lg p-8 md:p-10" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #2C5F2D" }}>
              <Rocket size={48} style={{ color: "#2C5F2D" }} className="mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ color: "#1F2937" }}>Project Implementation</h3>
              <p className="text-lg mb-6" style={{ color: "#4A5568" }}>Execute technology projects successfully</p>
              <h4 className="text-lg font-bold mb-4" style={{ color: "#1F2937" }}>What we do:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BulletItem text="Project planning and management" color="#2C5F2D" />
                <BulletItem text="System upgrades and migrations" color="#2C5F2D" />
                <BulletItem text="Office relocation IT support" color="#2C5F2D" />
                <BulletItem text="Merger & acquisition IT integration" color="#2C5F2D" />
                <BulletItem text="New location technology setup" color="#2C5F2D" />
                <BulletItem text="Change management support" color="#2C5F2D" />
              </div>
            </div>
          </div>
        </section>

        {/* Our Process Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="process-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
              Our Consulting Process
            </h2>
            <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
              A structured approach to solving your IT challenges
            </p>
            
            <div className="space-y-8">
              <ProcessStep num="1" Icon={Handshake} title="Initial Consultation" color="#3B82F6" items={["Understand your business", "Identify challenges and goals", "Determine if we're a good fit", "No obligation, no cost"]} isLast={false} />
              <ProcessStep num="2" Icon={ClipboardCheck} title="Assessment" color="#2C5F2D" items={["Gather detailed information", "Review current systems", "Interview key stakeholders", "Analyze your IT environment"]} isLast={false} />
              <ProcessStep num="3" Icon={BarChart3} title="Analysis & Recommendations" color="#3B82F6" items={["Identify issues and opportunities", "Develop solution options", "Create detailed recommendations", "Present findings and proposals"]} isLast={false} />
              <ProcessStep num="4" Icon={Calendar} title="Planning" color="#2C5F2D" items={["Create implementation plan", "Define project scope and timeline", "Establish budget and resources", "Get stakeholder approval"]} isLast={false} />
              <ProcessStep num="5" Icon={Settings} title="Execution & Support" color="#3B82F6" items={["Implement recommendations", "Manage projects to completion", "Provide training and documentation", "Ongoing support and optimization"]} isLast={true} />
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-5 bg-white" data-testid="benefits-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Why Work With an IT Consultant?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <BenefitCard Icon={Search} title="Objective Expertise" description="Get unbiased recommendations based on your needs, not vendor incentives" color="#3B82F6" />
              <BenefitCard Icon={DollarSign} title="Cost Savings" description="Avoid costly mistakes and ensure you're investing in the right solutions" color="#2C5F2D" />
              <BenefitCard Icon={Target} title="Strategic Alignment" description="Ensure technology supports your business goals instead of holding you back" color="#3B82F6" />
              <BenefitCard Icon={Shield} title="Risk Mitigation" description="Identify and address potential problems before they become crises" color="#2C5F2D" />
              <BenefitCard Icon={Brain} title="Knowledge Transfer" description="Learn best practices and build internal IT knowledge" color="#3B82F6" />
              <BenefitCard Icon={Focus} title="Focus on Core Business" description="Let experts handle IT strategy while you run your business" color="#2C5F2D" />
            </div>
          </div>
        </section>

        {/* Consulting vs Managed Services */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="comparison-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Consulting vs. Managed Services: What's the Difference?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* IT Consulting */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#3B82F6" }} data-testid="consulting-comparison">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#3B82F6" }}>IT Consulting</h3>
                <BulletItem text="Project-based or periodic engagement" color="#3B82F6" />
                <BulletItem text="Strategic guidance and planning" color="#3B82F6" />
                <BulletItem text="Technology assessments" color="#3B82F6" />
                <BulletItem text="Specific project implementation" color="#3B82F6" />
                <BulletItem text="Ends when project completes" color="#3B82F6" />
                <BulletItem text="Ideal for: One-time projects, strategic planning" color="#3B82F6" />
              </div>
              {/* Managed Services */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#2C5F2D" }} data-testid="managed-comparison">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#2C5F2D" }}>Managed Services</h3>
                <BulletItem text="Ongoing relationship" color="#2C5F2D" />
                <BulletItem text="Day-to-day IT support" color="#2C5F2D" />
                <BulletItem text="24/7 monitoring and maintenance" color="#2C5F2D" />
                <BulletItem text="Proactive problem resolution" color="#2C5F2D" />
                <BulletItem text="Continuous optimization" color="#2C5F2D" />
                <BulletItem text="Ideal for: Ongoing IT support, no IT staff" color="#2C5F2D" />
              </div>
            </div>
            {/* Bottom Note */}
            <div className="mt-8 p-5 rounded-md" style={{ backgroundColor: "#DBEAFE", borderLeft: "4px solid #3B82F6" }}>
              <p className="text-base italic" style={{ color: "#1F2937" }}>
                Many businesses use both—consulting for strategic projects and managed services for daily support.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-5 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              IT Consulting FAQ
            </h2>
            <div className="space-y-4">
              <FAQItem q="How much does IT consulting cost?" a="We offer both hourly consulting and fixed-price project work. After our free initial consultation, we'll provide a clear proposal with pricing." />
              <FAQItem q="How long does a typical consulting engagement last?" a="It varies. Some projects are completed in a few weeks, while others may span several months. We'll provide a timeline upfront." />
              <FAQItem q="Do you only work with businesses that don't have IT staff?" a="No. We often work alongside internal IT teams, providing specialized expertise or additional capacity for major projects." />
              <FAQItem q="What if we need ongoing support after the project?" a="We can transition you to our managed services or continue with periodic consulting as needed." />
              <FAQItem q="Can you help with vendor negotiations?" a="Yes. We can help you evaluate proposals, negotiate pricing, and ensure you're getting the best value." />
              <FAQItem q="Do you implement the solutions you recommend?" a="Yes. We can handle the entire project from planning to implementation, or just provide guidance if you prefer to implement internally." />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-5" style={{ background: "linear-gradient(to right, #3B82F6, #2563EB)" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Let's Discuss Your IT Strategy
            </h2>
            <p className="text-lg mb-10" style={{ color: "rgba(255,255,255,0.9)" }}>
              Schedule a free consultation to explore how we can help
            </p>
            <Button
              onClick={handleConsultation}
              data-testid="cta-btn"
              className="text-lg font-bold px-10 py-6 rounded-lg hover:bg-gray-100 transition-all"
              style={{ backgroundColor: "white", color: "#3B82F6" }}
            >
              Schedule Free Consultation
            </Button>
            <p className="mt-5 text-white">
              Call{" "}
              <a href="tel:7783497449" className="font-semibold hover:underline" data-testid="cta-phone">
                (778) 349-7449
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Helper Components
const ChecklistItem = ({ text }) => (
  <div className="flex items-start gap-3 py-2 px-4 mb-3 bg-white rounded-md">
    <CheckSquare size={20} style={{ color: "#2C5F2D", flexShrink: 0, marginTop: "2px" }} />
    <span className="text-base" style={{ color: "#374151" }}>{text}</span>
  </div>
);

const BulletItem = ({ text, color }) => (
  <div className="flex items-start gap-3 py-1">
    <span style={{ color, marginTop: "6px" }}>•</span>
    <span style={{ color: "#374151" }}>{text}</span>
  </div>
);

const ProcessStep = ({ num, Icon, title, color, items, isLast }) => (
  <div className="flex gap-6">
    <div className="flex-shrink-0">
      <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg" style={{ backgroundColor: color }}>
        {num}
      </div>
    </div>
    <div className="flex-grow pb-8" style={{ borderLeft: !isLast ? "3px solid #E5E7EB" : "none", marginLeft: "-35px", paddingLeft: "43px" }}>
      <div className="flex items-center gap-3 mb-3">
        <Icon size={28} style={{ color }} />
        <h3 className="text-xl font-bold" style={{ color: "#1F2937" }}>{title}</h3>
      </div>
      <ul className="space-y-2">
        <li className="text-base" style={{ color: "#4A5568" }}>• {items[0]}</li>
        <li className="text-base" style={{ color: "#4A5568" }}>• {items[1]}</li>
        <li className="text-base" style={{ color: "#4A5568" }}>• {items[2]}</li>
        <li className="text-base" style={{ color: "#4A5568" }}>• {items[3]}</li>
      </ul>
    </div>
  </div>
);

const BenefitCard = ({ Icon, title, description, color }) => (
  <div className="rounded-lg p-8 text-center" style={{ backgroundColor: "#F9FAFB", borderTop: `4px solid ${color}` }}>
    <Icon size={48} style={{ color }} className="mx-auto mb-4" />
    <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{description}</p>
  </div>
);

const FAQItem = ({ q, a }) => (
  <div className="rounded-lg p-6" style={{ backgroundColor: "#F9FAFB" }}>
    <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>{q}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{a}</p>
  </div>
);

export default ITConsultingPage;
